import React, { useState } from 'react';
import { Modal } from '@common/Modal';
import { Button } from '@common/Button';
import { Input, TextArea, Select } from '@common/FormFields';
import { Calendar, Clock, MapPin, Truck, AlertTriangle } from 'lucide-react';
import { logError, logInfo } from '@utils/logger';
import { Toast } from '@common/Toast';

export const BookSurveyModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    origin: '',
    destination: '',
    date: '',
    time: '',
    vehicleType: '',
    vehicleDimensions: {
      length: '',
      width: '',
      height: '',
      weight: ''
    },
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      const requiredFields = ['customerName', 'contactName', 'contactEmail', 'contactPhone', 'origin', 'destination', 'date'];
      const missingFields = requiredFields.filter(field => !formData[field]);
      
      if (missingFields.length > 0) {
        throw new Error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      }

      // Here you would normally submit the booking to your backend
      // For now, we'll just simulate a successful booking
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setToast({
        type: 'success',
        message: 'Survey booking request submitted successfully'
      });
      
      logInfo('Survey booking submitted:', formData);
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      logError('Survey booking failed:', error);
      setError(error.message || 'Failed to submit booking request');
      setToast({
        type: 'error',
        message: error.message || 'Failed to submit booking request'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Book Survey">
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Customer Information */}
        <div>
          <h3 className="text-white font-medium mb-3">Customer Information</h3>
          <div className="space-y-4">
            <Input
              label="Customer Name"
              value={formData.customerName}
              onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />
            
            <Input
              label="Contact Name"
              value={formData.contactName}
              onChange={(e) => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Contact Email"
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                required
                className="bg-gray-700 border-gray-600 text-white"
                labelClassName="text-gray-300"
              />
              
              <Input
                label="Contact Phone"
                type="tel"
                value={formData.contactPhone}
                onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                required
                className="bg-gray-700 border-gray-600 text-white"
                labelClassName="text-gray-300"
              />
            </div>
          </div>
        </div>

        {/* Route Information */}
        <div>
          <h3 className="text-white font-medium mb-3">Route Information</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-400 shrink-0" />
                <Input
                  label="Origin"
                  value={formData.origin}
                  onChange={(e) => setFormData(prev => ({ ...prev, origin: e.target.value }))}
                  required
                  className="bg-gray-700 border-gray-600 text-white w-full"
                  labelClassName="text-gray-300"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-red-400 shrink-0" />
                <Input
                  label="Destination"
                  value={formData.destination}
                  onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
                  required
                  className="bg-gray-700 border-gray-600 text-white w-full"
                  labelClassName="text-gray-300"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-400 shrink-0" />
                <Input
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  required
                  className="bg-gray-700 border-gray-600 text-white w-full"
                  labelClassName="text-gray-300"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-400 shrink-0" />
                <Input
                  label="Time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white w-full"
                  labelClassName="text-gray-300"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Vehicle Information */}
        <div>
          <h3 className="text-white font-medium mb-3">Vehicle Information</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-purple-400 shrink-0" />
              <Select
                label="Vehicle Type"
                value={formData.vehicleType}
                onChange={(value) => setFormData(prev => ({ ...prev, vehicleType: value }))}
                options={[
                  { value: '', label: 'Select Vehicle Type' },
                  { value: 'Flatbed', label: 'Flatbed' },
                  { value: 'Step Deck', label: 'Step Deck' },
                  { value: 'Double Drop', label: 'Double Drop' },
                  { value: 'RGN LowBed', label: 'RGN LowBed' },
                  { value: 'Extendable Trailers', label: 'Extendable Trailers' }
                ]}
                className="bg-gray-700 border-gray-600 text-white w-full"
                labelClassName="text-gray-300"
              />
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Input
                label="Length (ft)"
                type="number"
                value={formData.vehicleDimensions.length}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  vehicleDimensions: {
                    ...prev.vehicleDimensions,
                    length: e.target.value
                  }
                }))}
                className="bg-gray-700 border-gray-600 text-white"
                labelClassName="text-gray-300"
              />
              
              <Input
                label="Width (ft)"
                type="number"
                value={formData.vehicleDimensions.width}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  vehicleDimensions: {
                    ...prev.vehicleDimensions,
                    width: e.target.value
                  }
                }))}
                className="bg-gray-700 border-gray-600 text-white"
                labelClassName="text-gray-300"
              />
              
              <Input
                label="Height (ft)"
                type="number"
                value={formData.vehicleDimensions.height}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  vehicleDimensions: {
                    ...prev.vehicleDimensions,
                    height: e.target.value
                  }
                }))}
                className="bg-gray-700 border-gray-600 text-white"
                labelClassName="text-gray-300"
              />
              
              <Input
                label="Weight (lbs)"
                type="number"
                value={formData.vehicleDimensions.weight}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  vehicleDimensions: {
                    ...prev.vehicleDimensions,
                    weight: e.target.value
                  }
                }))}
                className="bg-gray-700 border-gray-600 text-white"
                labelClassName="text-gray-300"
              />
            </div>
          </div>
        </div>

        {/* Additional Notes */}
        <div>
          <h3 className="text-white font-medium mb-3">Additional Notes</h3>
          <TextArea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            rows={4}
            className="bg-gray-700 border-gray-600 text-white"
            labelClassName="text-gray-300"
            placeholder="Any special requirements or information..."
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            onClick={onClose}
            variant="default"
            className="bg-gray-700 hover:bg-gray-600 text-white"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {loading ? 'Submitting...' : 'Book Survey'}
          </Button>
        </div>
      </form>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Modal>
  );
};